import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { RouteNames } from './core/constants/route-names';
import { roleGuard } from './core/guards/role.guard';
import { adminGuard } from './core/guards/admin.guard';
import { practiceOwnerGuard } from './core/guards/practice-owner.guard';
import { partnerOnboardingGuard } from './core/guards/partner-onboarding.guard';
import { translationScopeGuard } from './core/guards/translation-scope.guard';


const routes: Routes = [
  {
    path: RouteNames.AUTH_ROUTE,
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
    canActivate: [translationScopeGuard('auth')],
  },
  {
    path: RouteNames.USER_ACCOUNT_ROUTE,
    loadChildren: () =>
      import('./features/user-account/user-account.module').then((m) => m.UserAccountModule),
    canActivate: [translationScopeGuard('user-account'), roleGuard]
  },
  {
    path: RouteNames.DASHBOARD_ROUTE,
    loadChildren: () =>
      import('./features/dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [translationScopeGuard('dashboard'), roleGuard, partnerOnboardingGuard]
  },
  {
    path: RouteNames.CONTENT_ROUTE,
    loadChildren: () =>
      import('./features/content/content.module').then((m) => m.ContentModule),
    canActivate: [translationScopeGuard('content'), roleGuard]
  },
  {
    path: RouteNames.CLIENTS_ROUTE,
    loadChildren: () =>
      import('./features/clients/clients.module').then((m) => m.ClientsModule),
    canActivate: [translationScopeGuard('clients'), roleGuard]
  },
  {
    path: RouteNames.PRACTICE_ROUTE,
    loadChildren: () =>
      import('./features/practice/practice.module').then((m) => m.PracticeModule),
    canActivate: [translationScopeGuard('practice'), practiceOwnerGuard]
  },
  {
    path: RouteNames.ORGANIZATIONS_ROUTE,
    loadChildren: () =>
      import('./features/organizations/organizations.module').then((m) => m.OrganizationsModule),
    canActivate: [translationScopeGuard('organizations'), roleGuard]
  },
  {
    path: RouteNames.DAM_ROUTE,
    loadChildren: () => import('./features/dam/dam.module').then((m) => m.DamModule),
    canActivate: [translationScopeGuard('dam'), roleGuard]
  },
  {
    path: RouteNames.FAQ_ROUTE,
    loadChildren: () =>
      import('./features/faq/faq.module').then((m) => m.FaqModule),
    canActivate: [translationScopeGuard('faq'), roleGuard]
  },
  {
    path: RouteNames.INTERNATIONALIZATION_ROUTE,
    loadChildren: () =>
      import('./features/internationalization/internationalization.module').then((m) => m.InternationalizationModule),
    canActivate: [translationScopeGuard('internationalization'), roleGuard]
  },
  {
    path: RouteNames.FINANCE_ROUTE,
    loadChildren: () =>
      import('./features/finance/finance.module').then((m) => m.FinanceModule),
    canActivate: [translationScopeGuard('finance'), adminGuard]
  },
  {
    path: RouteNames.SHOP_ROUTE,
    loadChildren: () =>
      import('./features/shop/shop.module').then((m) => m.ShopModule),
    canActivate: [translationScopeGuard('shop'), roleGuard]
  },
  {
    path: RouteNames.HELP_ROUTE,
    loadChildren: () =>
      import('./features/help/help.module').then((m) => m.HelpModule),
    canActivate: [translationScopeGuard('help'), roleGuard]
  },
  {
    path: RouteNames.PARTNERS_ROUTE,
    loadChildren: () =>
      import('./features/partners/partners.module').then((m) => m.PartnersModule),
    canActivate: [translationScopeGuard('partners'), adminGuard],
  },
  {
    path: RouteNames.PARTNER_PORTAL_ROUTE,
    loadChildren: () =>
      import('./features/partners-portal/partners-portal.module').then((m) => m.PartnersPortalModule),
    canActivate: [translationScopeGuard('partners-portal'), roleGuard],
  },
  {
    path: RouteNames.PUBLIC_PLANS_ROUTE,
    loadChildren: () =>
      import('./features/public/public.module').then((m) => m.PublicModule),
    canActivate: [translationScopeGuard('public')],
  },
  {
    path: `${RouteNames.PUBLIC_REFERRAL_ROUTE}/:code`,
    loadChildren: () =>
      import('./features/public/public.module').then((m) => m.PublicModule),
    canActivate: [translationScopeGuard('public')],
  },
  {
    path: `${RouteNames.PUBLIC_PARTNER_INVITE_ROUTE}/:code`,
    loadChildren: () =>
      import('./features/partner-invite/partner-invite.module').then((m) => m.PartnerInviteModule),
    canActivate: [translationScopeGuard('partner-invite')],
  },
  {
    path: RouteNames.PUBLIC_PATIENT_INVITE_ROUTE,
    loadChildren: () =>
      import('./features/patient-invite/patient-invite.module').then((m) => m.PatientInviteModule),
    canActivate: [translationScopeGuard('patient-invite')],
  },
  {
    path: RouteNames.REGISTER_ROUTE,
    loadChildren: () =>
      import('./features/register/register.module').then((m) => m.RegisterModule),
    canActivate: [translationScopeGuard('register')],
  },
  {
    path: '**',
    redirectTo: RouteNames.DASHBOARD_ROUTE,
    pathMatch: 'full',
  },
];

@NgModule({
  // `paramsInheritanceStrategy: 'always'` lets a child component keep reading a param that now
  // sits on a nested parent route (see the breadcrumb route nesting), so pages need no change
  // when their routes are grouped under a section.
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
